import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { CdkDrag, DragDropModule } from '@angular/cdk/drag-drop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { Document } from '../model';
import { CompanyWriteAccess } from '../common/service/company-write-access.service';

@Component({
  selector: 'app-document-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, DragDropModule, MatButtonModule, MatIconModule, TranslateModule],
  hostDirectives: [
    {
      directive: CdkDrag,
      inputs: ['cdkDragData', 'cdkDragBoundary', 'cdkDragPreviewClass'],
      outputs: ['cdkDragStarted', 'cdkDragMoved', 'cdkDragEnded'],
    },
  ],
  template: `
    <article class="document-card" [class.sidebar]="variant === 'sidebar'" [class.canvas]="variant === 'canvas'">
      @if (variant === 'canvas') {
        <div class="document-card__canvas-layout">
          <div class="document-card__preview-box">
            @if (previewSrc) {
              <img class="document-card__preview" [src]="previewSrc" [alt]="document.title" />
            } @else {
              <mat-icon>{{ document.fileType?.includes('pdf') ? 'picture_as_pdf' : 'image' }}</mat-icon>
            }
          </div>
          <div class="document-card__body">
            <h4>{{ document.title }}</h4>
            <p class="document-card__type">{{ document.type?.name || ('canvas_no_type_tr' | translate) }}</p>
            <p class="document-card__date">{{ document.date | date: 'mediumDate' }}</p>
          </div>
          @if (positionLabel) {
            <div class="document-card__meta">
              <span>{{ positionLabel }}</span>
            </div>
          }
          <button
            mat-icon-button
            type="button"
            class="document-card__delete"
            aria-label="Remove document"
            [disabled]="readOnly()"
            (click)="deleteDocument.emit(); $event.stopPropagation()"
          >
            <mat-icon>close</mat-icon>
          </button>
          @if (selectable) {
            <label
              class="document-card__select"
              (click)="$event.stopPropagation()"
              (pointerdown)="$event.stopPropagation()"
            >
              <input
                type="checkbox"
                [checked]="selected"
                (change)="selectionChange.emit($any($event.target).checked)"
              />
            </label>
          }
        </div>
      } @else {
        <div class="document-card__icon">
          @if (previewSrc) {
            <img class="document-card__preview" [src]="previewSrc" [alt]="document.title" />
          } @else {
            <mat-icon>{{ document.fileType?.includes('pdf') ? 'picture_as_pdf' : 'image' }}</mat-icon>
          }
        </div>
        <div class="document-card__body">
          <h4>{{ document.title }}</h4>
          <p>{{ document.fileName || document.title }}</p>
          <p class="document-card__details">
            <span>{{ document.type?.name || ('canvas_no_type_tr' | translate) }}</span>
            <span>•</span>
            <span>{{ document.date | date: 'mediumDate' }}</span>
            <span>•</span>
            <span>{{ document.pages }} {{ 'canvas_pages_suffix_tr' | translate }}</span>
          </p>
        </div>
      }
    </article>
    <ng-template cdkDragPreview [matchSize]="true">
      <article class="document-card canvas document-drag-preview">
        <div class="document-card__canvas-layout">
          <div class="document-card__preview-box">
            @if (previewSrc) {
              <img class="document-card__preview" [src]="previewSrc" [alt]="document.title" />
            } @else {
              <mat-icon>{{ document.fileType?.includes('pdf') ? 'picture_as_pdf' : 'image' }}</mat-icon>
            }
          </div>
          <div class="document-card__body">
            <h4>{{ document.title }}</h4>
            <p class="document-card__type">{{ document.type?.name || ('canvas_no_type_tr' | translate) }}</p>
            <p class="document-card__date">{{ document.date | date: 'mediumDate' }}</p>
          </div>
          <button
            mat-icon-button
            type="button"
            class="document-card__delete document-card__delete-placeholder"
            aria-hidden="true"
          >
            <mat-icon>close</mat-icon>
          </button>
          @if (selectable) {
            <label class="document-card__select" aria-hidden="true">
              <input type="checkbox" [checked]="selected" tabindex="-1" />
            </label>
          }
        </div>
      </article>
    </ng-template>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .document-card {
        display: flex;
        gap: 12px;
        align-items: center;
        padding: 12px;
        border: 1px solid #d7dde3;
        border-radius: 8px;
        background: #fff;
        user-select: none;
      }
      .document-drag-preview {
        opacity: 0.96;
        box-shadow: 0 16px 36px rgba(15, 23, 42, 0.24);
      }
      .document-card.sidebar {
        align-items: flex-start;
      }
      .document-card.sidebar .document-card__icon {
        margin-top: 2px;
      }
      .document-card.canvas {
        position: relative;
        width: 160px;
        min-width: 0;
        max-width: none;
        padding: 10px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
        cursor: grab;
      }
      :host(.cdk-drag-dragging) .document-card.canvas {
        cursor: grabbing;
      }
      .document-card__canvas-layout {
        position: relative;
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: 88px auto auto;
        align-items: start;
        width: 100%;
      }
      .document-card__preview-box {
        grid-column: 1;
        grid-row: 1;
        width: 100%;
        height: 100%;
        aspect-ratio: 1 / 0.84;
        display: grid;
        place-items: center;
        border-radius: 10px;
        background: #eef3f8;
        overflow: hidden;
        flex: 0 0 auto;
      }
      .document-card__canvas-layout .document-card__preview {
        width: 100%;
        height: 90px;
        object-fit: cover;
        display: block;
      }
      .document-card__canvas-layout .document-card__body {
        grid-column: 1;
        grid-row: 2;
        min-width: 0;
        width: 100%;
        margin-top: 8px;
      }
      .document-card__canvas-layout .document-card__body h4 {
        margin-bottom: 2px;
        font-size: 12px;
        line-height: 1.2;
      }
      .document-card__type,
      .document-card__date {
        font-size: 11px;
        color: #6b7280;
        line-height: 1.25;
        margin: 0;
      }
      .document-card__canvas-layout .document-card__meta {
        grid-column: 1;
        grid-row: 3;
        align-self: end;
        justify-self: start;
        padding-top: 0;
      }
      .document-card__canvas-layout .document-card__meta span {
        font-size: 12px;
        color: #6b7280;
      }
      .document-card__icon {
        width: 36px;
        height: 36px;
        display: grid;
        place-items: center;
        border-radius: 6px;
        background: #eef3f8;
        flex: 0 0 auto;
        overflow: hidden;
      }
      .document-card__preview {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
      .document-card__body {
        min-width: 0;
        flex: 1 1 auto;
        width: 100%;
      }
      .document-card__body h4,
      .document-card__body p,
      .document-card__meta span {
        margin: 0;
      }
      .document-card__body h4 {
        font-size: 14px;
        font-weight: 600;
        line-height: 1.25;
        white-space: normal;
        overflow: visible;
        color: #111827;
      }
      .document-card__body p,
      .document-card__meta span {
        font-size: 12px;
        color: #6b7280;
        line-height: 1.35;
        white-space: normal;
        overflow: visible;
      }
      .document-card__details {
        display: flex;
        gap: 6px;
        flex-wrap: wrap;
        white-space: normal;
        line-height: 1.25;
        margin-top: 4px;
      }
      .document-card__meta {
        flex: 0 0 auto;
        align-self: flex-end;
        padding-top: 2px;
      }
      .document-card__delete {
        position: absolute;
        top: -6px;
        right: -6px;
        width: 26px;
        height: 26px;
        line-height: 26px;
        color: #64748b;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: rgba(238, 243, 248, 0.5);
      }
      .document-card__select {
        position: absolute;
        top: 0px;
        left: 0px;
        z-index: 3;
        display: block;
      }
      .document-card__select input {
        width: 16px;
        height: 16px;
        margin: 0;
        accent-color: #476873;
      }
      .document-card__delete mat-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
        line-height: 16px;
      }
    `,
  ],
})
export class DocumentCardComponent {
  /**
   * Mirrors the server's `Company.mayWrite` — see `CompanyWriteAccess`. Read directly rather than
   * passed down: this component sits several levels below the page that knows the company.
   */
  readonly readOnly = inject(CompanyWriteAccess).readOnly;

  @Input() selectable = false;
  @Input() selected = false;
  @Output() selectionChange = new EventEmitter<boolean>();
  @Input({ required: true }) document!: Document;
  @Input() variant: 'sidebar' | 'canvas' = 'sidebar';
  @Input() positionLabel = '';
  @Input() previewSrc = '';
  @Output() deleteDocument = new EventEmitter<void>();
}
